import type {CallbackDataLoading, Transaction} from "../base/ui_general/GeneralInterfaces.tsx";
import type {BaseComponentProps} from "../base/ui_general/BaseComponent.tsx";
import {UserLanguages} from "../base/Settings.tsx";


/**
 * Enum representing the available tabs in the DesignCenter view.
 * Each tab corresponds to a major functional area in the securitization design workflow.
 */
export enum DesignCenterTabEnum {
    /** General transaction overview and setup */
    GENERAL = "general",
    /** Liabilities structure and details */
    LIABILITIES = "liabilities",
    /** Asset pool and related data */
    ASSETS = "assets",
    /** Time Series data */
    TIMESERIES = "timeseries",
    /** Formula editor and management */
    FORMULA = "formula",
    /** Trigger logic and configuration */
    TRIGGERS = "triggers",
    /** Waterfall structure and payment priorities */
    WATERFALL = "waterfall",
    /** WMT data */
    WMT = "wmt",
    /** AI Chat tab for interacting with LLM */
    AI_CHAT = "ai_chat",
    /** Settings tab for configuration and preferences */
    SETTINGS = "settings",
}

/**
 * Props passed to individual tab components in the DesignCenter.
 * Includes the current transaction and base component props.
 */
export interface DesignCenterTabProps extends BaseComponentProps {
    /** The currently selected transaction, or undefined if none is selected. */
    transaction: Transaction | undefined;
    /** Callback to trigger batch data loading for the above entities. */
    loadData: CallbackDataLoading;
    /** Stores the current inner height of the browser window for responsive layout calculations. */
    windowInnerHeight: number;
    /** Indicates the preferred user language. */
    user_language: UserLanguages;
}

/**
 * Enum representing the available evaluation fields for transaction analysis.
 * Used to select and display key metrics in the evaluation results.
 */
export enum EvaluationFieldEnum {
    /** Weighted Average Life */
    WAL = "WAL",
    /** WAL distribution data */
    WALDistribution = "WAL distribution",
    /** Fair Value of the tranche */
    FairValue = "Fair Value",
    /** Fair value distribution data */
    FairValueDistribution = "Fair Value distribution",
    /**Fair premium */
    FairPremium = "Fair premium",
    /** Probability of Default */
    PD = "1-year PD",
    /** Probability of Default */
    PD_lifetime = "Lifetime PD",
    /** Loss Given Default */
    LGD = "LGD",
    /** Expected Loss */
    EL = "EL",
    /** Loss distribution data */
    LossDistribution = "Loss distribution",
    /** Value at Risk */
    VaR = "Value at Risk",
    /** Risk-adjusted Fair Value */
    RiskAdjustedFairValue = "Risk-adjusted Fair Value",
    /** Risk-adjusted fair premium */
    RiskAdjustedFairPremium = "Risk-adjusted fair premium"
}
